import React, { useEffect, useRef } from 'react';
import './OpponentCounterStrikeDialog.css';

/**
 * Opponent Counter Strike Dialog Component
 * Displays when the opponent has a pending Counter Strike order
 * Blocks the player from interacting with the game until counterstrike is resolved
 * 
 * Mobile support: When isMobile is true, displays with smaller font sizes
 * to fit the 932x430 mobile viewport.
 * 
 * ✅ SAFETY: Includes 5-second timeout protection to prevent freezes
 */
const OpponentCounterStrikeDialog = ({ show, ws, gameId, gameState, userId, isMobile = false }) => {
  const timeoutRef = useRef(null);
  const hasTriggeredAutoKeep = useRef(false);

  // ✅ SAFETY: Log warning if bot counterstrike takes too long
  // Backend timeout protection will handle auto-resolving stuck bots
  useEffect(() => {
    if (!show || !gameState) {
      // Clear timeout if dialog is hidden
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      hasTriggeredAutoKeep.current = false;
      return;
    }

    // Find opponent ID
    const opponentId = gameState.players?.find(id => id !== userId);
    if (!opponentId) {
      return;
    }

    // Check if opponent is a bot
    const isBot = opponentId === 'bot' || opponentId === 'campaign_bot';
    if (!isBot) {
      // Not a bot, don't set timeout (human players should make their own decisions)
      return;
    }

    // Check if opponent has pending counterstrike
    const opponentState = gameState.state?.[opponentId];
    const pendingCounterStrike = opponentState?.pendingCounterStrike || [];
    
    // ✅ CRITICAL: If opponent no longer has pending counterstrike, the dialog should hide
    // This check ensures the dialog disappears immediately when counterstrike is resolved
    if (pendingCounterStrike.length === 0) {
      // No pending counterstrike, clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      hasTriggeredAutoKeep.current = false;
      // Dialog will hide because show prop will be false (opponentHasCounterStrike will be false)
      return;
    }

    // Set up 5-second timeout to log warning (backend will handle the actual resolution)
    if (!hasTriggeredAutoKeep.current) {
      timeoutRef.current = setTimeout(() => {
        console.warn(`⏰ Opponent Counter Strike: 5-second timeout reached for bot ${opponentId} - backend should auto-resolve`);
        hasTriggeredAutoKeep.current = true;
        // Backend timeout protection in sendGameStateToPlayers will handle the actual resolution
      }, 5000); // 5 seconds
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [show, gameState, userId]);

  if (!show) {
    return null;
  }

  return (
    <div className="opponent-counter-strike-overlay" data-no-battle-flashlight="true">
      <div 
        className="opponent-counter-strike-dialog"
        style={isMobile ? {
          transform: 'scale(0.5)',
          transformOrigin: 'center center'
        } : {}}
      >
        <div className="opponent-counter-strike-header">
          <h2 style={isMobile ? { fontSize: '28px' } : {}}>Your opponent has a chance to strike back!</h2>
        </div>
        <div className="opponent-counter-strike-content">
          <div className="opponent-counter-strike-icon" style={isMobile ? { fontSize: '60px' } : {}}>⚔️</div>
          <p style={isMobile ? { fontSize: '20px' } : {}}>Please wait while your opponent decides...</p>
        </div>
      </div>
    </div>
  );
};

export default OpponentCounterStrikeDialog;
